<template>
  <div>
    <div class="mb-4">
      <h2 class="text-base font-semibold text-gray-800">User Login Activity</h2>
      <p class="text-sm text-gray-500 mt-0.5">
        A record of every time a staff member has logged in — who, when, and for how long.
      </p>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap items-end gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Search</label>
        <input
          v-model="filters.query"
          type="text"
          placeholder="Username or email"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-56"
          @keyup.enter="applyFilters"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">From</label>
        <input
          v-model="filters.from_date"
          type="date"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">To</label>
        <input
          v-model="filters.to_date"
          type="date"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
        />
      </div>
      <button
        @click="applyFilters"
        class="px-4 py-1.5 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 transition"
      >
        Search
      </button>
      <button
        v-if="hasActiveFilters"
        @click="clearFilters"
        class="px-4 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition"
      >
        Clear
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto shadow-md rounded-sm">
      <table class="w-full text-left text-gray-700">
        <thead class="bg-gray-100">
          <tr>
            <th class="px-6 py-3">#</th>
            <th class="px-6 py-3">Username</th>
            <th class="px-6 py-3">Email</th>
            <th class="px-6 py-3">Role</th>
            <th class="px-6 py-3">Logged In At</th>
            <th class="px-6 py-3">Logged Out At</th>
            <th class="px-6 py-3">Duration</th>
            <th class="px-6 py-3">IP Address</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(log, index) in logs" :key="log.id" class="border-b hover:bg-gray-50">
            <td class="px-6 py-3">{{ (currentPage - 1) * perPage + index + 1 }}</td>
            <td class="px-6 py-3 font-medium">{{ log.user?.Username || '—' }}</td>
            <td class="px-6 py-3">{{ log.user?.Email || '—' }}</td>
            <td class="px-6 py-3">{{ log.user?.UserType || '—' }}</td>
            <td class="px-6 py-3">{{ formatDateTime(log.logged_in_at) }}</td>
            <td class="px-6 py-3">
              <span v-if="log.logged_out_at">{{ formatDateTime(log.logged_out_at) }}</span>
              <span v-else class="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Active</span>
            </td>
            <td class="px-6 py-3">{{ duration(log) }}</td>
            <td class="px-6 py-3 text-gray-500">{{ log.ip_address || '—' }}</td>
          </tr>
          <tr v-if="!logs.length">
            <td colspan="8" class="px-6 py-10 text-center text-gray-400">
              No login activity recorded yet.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <PaginationComponent
      v-model:currentPage="currentPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      :itemsPerPage="perPage"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />
  </div>
</template>

<script>
import apiClient from '@/services/api'
import PaginationComponent from '@/components/shared/pagination.vue'
import { formatDateTime } from '@/services/customUtilities'

export default {
  name: 'UserLoginActivity',

  components: { PaginationComponent },

  data() {
    return {
      logs:        [],
      loading:     false,
      currentPage: 1,
      totalPages:  1,
      totalItems:  0,
      perPage:     25,
      filters: {
        query:     '',
        from_date: '',
        to_date:   '',
      },
    }
  },

  computed: {
    hasActiveFilters() {
      return !!(this.filters.query || this.filters.from_date || this.filters.to_date)
    },
  },

  mounted() {
    this.fetchLogs()
  },

  methods: {
    formatDateTime,

    async fetchLogs() {
      try {
        this.loading = true
        const response = await apiClient.getLoginLogs({
          page:      this.currentPage,
          per_page:  this.perPage,
          query:     this.filters.query,
          from_date: this.filters.from_date,
          to_date:   this.filters.to_date,
        })
        if (response.success) {
          this.logs       = response.data
          this.totalPages = response.meta?.total_pages || 1
          this.totalItems = response.meta?.total_items || 0
        } else {
          this.$toast.error(response.message || 'Failed to load login activity')
        }
      } catch {
        this.$toast.error('Failed to fetch login activity')
      } finally {
        this.loading = false
      }
    },

    applyFilters() {
      this.currentPage = 1
      this.fetchLogs()
    },

    clearFilters() {
      this.filters = { query: '', from_date: '', to_date: '' }
      this.currentPage = 1
      this.fetchLogs()
    },

    goToPage(page) {
      this.currentPage = page
      this.fetchLogs()
    },

    changePageSize(size) {
      this.perPage     = Number(size)
      this.currentPage = 1
      this.fetchLogs()
    },

    // Human-readable elapsed time between login and logout (or "now" if still active).
    duration(log) {
      if (!log.logged_in_at) return '—'
      const start = new Date(log.logged_in_at)
      const end   = log.logged_out_at ? new Date(log.logged_out_at) : new Date()
      const ms    = end - start
      if (ms < 0) return '—'

      const totalMinutes = Math.floor(ms / 60000)
      const hours   = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours === 0 && minutes === 0) return '<1 min'
      if (hours === 0) return `${minutes} min`
      return `${hours}h ${minutes}m`
    },
  },
}
</script>
